import { effect, signal } from '@preact/signals'
import type { ReadonlySignal } from '@preact/signals'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { RoomPlayerReport } from '../store/roomStore'
import type { WebinarRoomVideoState } from '../types'
import { IDLE_MEDIA, playerStateOf, readMedia, shouldReport } from './media'
import type { PlayerMedia } from './media'
import { loadMuxPlayer } from './muxPlayer'
import type { MuxPlayerLike } from './muxPlayer'
import { nextLiveGeneration } from './source'

const MEDIA_EVENTS = ['loadstart', 'play', 'pause', 'playing', 'waiting', 'canplay', 'ended', 'emptied', 'volumechange', 'loadedmetadata']
const TIME_EVENTS = ['timeupdate', 'seeked', 'durationchange', 'loadedmetadata', 'emptied']

/**
 * One player's state and controls, for the room's own controls drawn around `<mux-player>`. The state is in signals:
 * `time` moves four times a second, so only what reads it re-renders, never the page around the player.
 */
export interface Player {
    readonly media: ReadonlySignal<PlayerMedia>
    readonly time: ReadonlySignal<number>
    /** The recording's length once the player knows it; null before, and for a live stream. */
    readonly duration: ReadonlySignal<number | null>
    attach(element: MuxPlayerLike): void
    /** Lets go of `element` if it is the one attached; a late call for an element already replaced does nothing. */
    detach(element: MuxPlayerLike): void
    play(): void
    pause(): void
    togglePlay(): void
    seek(seconds: number): void
    toggleMute(): void
    /** Full screen on `target`, which keeps the room's controls on screen, or the player's own (an iPhone's) without one. */
    toggleFullscreen(target: HTMLElement | null): void
    dispose(): void
}

export interface PlayerEnvironment {
    load: () => Promise<void>
    document: Pick<Document, 'fullscreenElement' | 'fullscreenEnabled' | 'exitFullscreen' | 'addEventListener' | 'removeEventListener'> | null
}

const browserEnvironment = (): PlayerEnvironment => ({ load: loadMuxPlayer, document: typeof document === 'undefined' ? null : document })

const sameMedia = (a: PlayerMedia, b: PlayerMedia) => (Object.keys(a) as (keyof PlayerMedia)[]).every(key => a[key] === b[key])

// A refused full screen or play changes nothing on screen, and the controls already show what did happen.
const ignoreRefusal = () => {}

export function createPlayer(environment: PlayerEnvironment = browserEnvironment()): Player {
    const media = signal<PlayerMedia>(IDLE_MEDIA)
    const time = signal(0)
    const duration = signal<number | null>(null)
    let element: MuxPlayerLike | null = null

    const setMedia = (next: PlayerMedia) => {
        if (!sameMedia(media.value, next)) media.value = next
    }
    const syncMedia = () => {
        if (!element || media.value.status !== 'ready') return
        setMedia({ ...media.value, ...readMedia(element), fullscreen: !!environment.document?.fullscreenElement })
    }
    const syncTime = () => {
        if (!element || media.value.status !== 'ready') return
        time.value = element.currentTime ?? 0
        const length = element.duration
        duration.value = Number.isFinite(length) && length > 0 ? length : null
    }
    const syncFullscreen = () => setMedia({ ...media.value, fullscreen: !!environment.document?.fullscreenElement })

    function detach(target: MuxPlayerLike) {
        if (element !== target) return
        for (const name of MEDIA_EVENTS) target.removeEventListener(name, syncMedia)
        for (const name of TIME_EVENTS) target.removeEventListener(name, syncTime)
        environment.document?.removeEventListener('fullscreenchange', syncFullscreen)
        element = null
        media.value = IDLE_MEDIA
        time.value = 0
        duration.value = null
    }

    const ready = () => (element && media.value.status === 'ready' ? element : null)
    const play = () => {
        ready()?.play().catch(ignoreRefusal)
    }
    const pause = () => {
        ready()?.pause()
    }

    return {
        media,
        time,
        duration,
        attach(target) {
            if (element === target) return
            if (element) detach(element)
            element = target
            for (const name of MEDIA_EVENTS) target.addEventListener(name, syncMedia)
            for (const name of TIME_EVENTS) target.addEventListener(name, syncTime)
            environment.document?.addEventListener('fullscreenchange', syncFullscreen)
            media.value = { ...IDLE_MEDIA, status: 'loading' }
            environment.load().then(
                () => {
                    if (element !== target) return
                    media.value = { ...media.value, status: 'ready' }
                    syncMedia()
                    syncTime()
                },
                () => {
                    if (element === target) media.value = { ...media.value, status: 'failed' }
                },
            )
        },
        detach,
        play,
        pause,
        togglePlay() {
            if (media.value.paused || media.value.ended) play()
            else pause()
        },
        seek(seconds) {
            const target = ready()
            if (!target) return
            const length = duration.value
            const at = Math.max(0, length === null ? seconds : Math.min(seconds, length))
            target.currentTime = at
            time.value = at
        },
        toggleMute() {
            const target = ready()
            if (target) target.muted = !target.muted
        },
        toggleFullscreen(target) {
            const page = environment.document
            if (page?.fullscreenElement) {
                page.exitFullscreen().catch(ignoreRefusal)
            } else if (target && page?.fullscreenEnabled && typeof target.requestFullscreen === 'function') {
                target.requestFullscreen().catch(ignoreRefusal)
            } else {
                ready()?.requestFullscreen().catch(ignoreRefusal)
            }
        },
        dispose() {
            if (element) detach(element)
        },
    }
}

/** A player for one component's lifetime. */
export function usePlayer(): Player {
    const [player] = useState(() => createPlayer())
    useEffect(() => () => player.dispose(), [player])
    return player
}

/** How long a ready player stays paused before the room offers a play button. */
export const PAUSED_PROMPT_DELAY_MS = 1000

/**
 * A ready player still paused a second on: the replay waiting for its viewer, a browser that refused to start it (iOS
 * Low Power Mode refuses even muted autoplay), or something paused it. With Mux Player's controls hidden the room has
 * to offer play itself; the delay keeps the button from flashing up while autoplay starts.
 */
export function usePausedPrompt(player: Player): boolean {
    const media = player.media.value
    const paused = media.status === 'ready' && media.paused && !media.ended
    const [prompt, setPrompt] = useState(false)
    useEffect(() => {
        setPrompt(false)
        if (!paused) return
        const timer = setTimeout(() => setPrompt(true), PAUSED_PROMPT_DELAY_MS)
        return () => clearTimeout(timer)
    }, [paused])
    return prompt && paused
}

/** Whether the player has played since it mounted: from then on, a pause is the viewer's own. */
export function useHasPlayed(player: Player): boolean {
    const media = player.media.value
    const [played, setPlayed] = useState(false)
    useEffect(() => {
        if (media.status === 'ready' && !media.paused) setPlayed(true)
    }, [media.status, media.paused])
    return played
}

/**
 * Tells the store what the player is doing, for the heartbeat: its state, and with `withPosition` (the replay) where
 * it is. Only a change worth sending is reported (`shouldReport`).
 */
export function usePlayerReport(player: Player, withPosition: boolean, report: (next: RoomPlayerReport) => void) {
    const latest = useRef(report)
    latest.current = report
    useEffect(() => {
        let last: RoomPlayerReport = { state: 'Slate', positionSeconds: null }
        return effect(() => {
            const media = player.media.value
            const next: RoomPlayerReport = {
                state: playerStateOf(media),
                positionSeconds: withPosition && media.status === 'ready' ? player.time.value : null,
            }
            if (!shouldReport(last, next)) return
            last = next
            latest.current(next)
        })
    }, [player, withPosition])
}

/** Which live player this is (`nextLiveGeneration`): a key that remounts the player when the stream comes back on. */
export function useLiveGeneration(videoState: WebinarRoomVideoState): number {
    const seen = useRef<{ state: WebinarRoomVideoState | null; generation: number }>({ state: null, generation: 0 })
    if (seen.current.state !== videoState) {
        seen.current = { state: videoState, generation: nextLiveGeneration(seen.current.state, videoState, seen.current.generation) }
    }
    return seen.current.generation
}
